import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import {
  fetchHomeImages, addHomeImage, updateHomeImage, deleteHomeImage,
  uploadProductImage, uploadUrl, fetchProducts,
} from "@/lib/api";

export default function HomeGalleryUploader() {
  const inputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [imgs, prods] = await Promise.all([fetchHomeImages(), fetchProducts()]);
      setImages(imgs);
      setProducts(prods);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        try {
          const { url } = await uploadProductImage(f);
          await addHomeImage({ url, alt: f.name.replace(/\.[^.]+$/, "") });
        } catch (e) {
          toast.error(e?.response?.data?.detail || `Couldn't upload ${f.name}`);
        }
      }
      toast.success("Homepage gallery updated");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Remove this image from the homepage?")) return;
    try {
      await deleteHomeImage(id);
      toast.success("Removed");
      await refresh();
    } catch (e) {
      toast.error("Failed to remove image");
    }
  };

  const handleProductChange = async (id, productId) => {
    try {
      await updateHomeImage(id, { product_id: productId || "" });
      toast.success(productId ? "Linked to product" : "Link cleared");
      setImages((prev) => prev.map((i) => (i.id === id ? { ...i, product_id: productId || null } : i)));
    } catch (e) {
      toast.error("Failed to update link");
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading homepage gallery…</p>;

  return (
    <div data-testid="home-gallery-uploader">
      <div
        data-testid="home-gallery-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"
        }`}
      >
        <Upload size={20} strokeWidth={1.5} className="text-muted-foreground" />
        <p className="text-sm text-foreground">{busy ? "Uploading…" : "Drag images here or click to upload"}</p>
        <p className="text-[11px] text-muted-foreground">JPG, PNG, WEBP · ideally 1200×1500 (4:5)</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="home-gallery-input"
        />
      </div>

      {images.length > 0 ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img) => (
            <div key={img.id} className="rounded-xl border border-border bg-secondary/30 overflow-hidden">
              <div className="relative group aspect-[4/5] bg-secondary">
                <img src={uploadUrl(img.url)} alt={img.alt || ""} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => handleRemove(img.id)}
                  data-testid={`home-gallery-remove-${img.id}`}
                  className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove from homepage"
                >
                  <Trash2 size={12} strokeWidth={1.5} />
                </button>
                {img.product_id && (
                  <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/65 text-white text-[10px] uppercase tracking-wider">
                    <LinkIcon size={10} strokeWidth={1.8} /> Linked
                  </span>
                )}
              </div>
              <div className="p-3">
                <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground block mb-1.5">
                  Click-through product
                </label>
                <select
                  value={img.product_id || ""}
                  onChange={(e) => handleProductChange(img.id, e.target.value)}
                  data-testid={`home-gallery-product-${img.id}`}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-xs focus:outline-none focus:border-primary"
                >
                  <option value="">— Not linked (no click-through) —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-xs text-muted-foreground">No images yet — drop a few photos to fill the homepage gallery.</p>
      )}
    </div>
  );
}
